import { z } from "zod";

import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

export const client = "s3";
export const table_name = "s3_bank_positions";
export const portfolio_table_name = "s3_portfolios";
export const static_data_table_name = "s3_static_data";
export const includeClientFilter = true;
export const includeClientBankFields = true;

export const names = {
  portfolioId: "s3_portfolio_id",
  portfolioName: "s3_portfolio_name",
  portfolioClient: "s3_portfolio_client",
  portfolioBankName: "s3_portfolio_bank_name",
  portfolioVehicleContains: "s3_portfolio_vehicle_contains",
  portfolioClientContains: "s3_portfolio_client_contains",
  portfolioAccountNameContains: "s3_portfolio_account_name_contains",
  portfolioNameContains: "s3_portfolio_name_contains",
  portfolioBankNameContains: "s3_portfolio_bank_name_contains",
  portfolioCurrency: "s3_portfolio_currency",
  portfolioAccountType: "s3_portfolio_account_type",
  portfolioReportingCurrency: "s3_portfolio_reporting_currency",
  staticDatumId: "s3_static_datum_id",
  staticDatumName: "s3_static_datum_name",
  staticDatumNameContains: "s3_static_datum_name_contains",
  staticDataAssetClassEquals: "asset_class_1_equals",
  staticDataAssetSubclassEquals: "asset_class_2_equals",
  staticDatumCurrency: "s3_static_datum_currency",
  staticDatumAssetClass2: "s3_static_datum_asset_class_2",
} as const;

export type BankPositionRow = {
  id: number;
  date: string;
  quantity?: number;
  market_value_local_ccy?: number;
  market_value_ptf_ccy?: number;
  fixing_local_ccy?: number;
  fixing_ptf_ccy?: number;
  local_currency?: string;
  [key: string]: unknown;
};

export const filterSchema = z.object({
  [names.portfolioClientContains]: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  [names.portfolioVehicleContains]: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  [names.portfolioAccountNameContains]: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  [names.portfolioNameContains]: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  [names.portfolioBankNameContains]: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  [names.staticDataAssetClassEquals]: z.string().optional(),
  [names.staticDataAssetSubclassEquals]: z.string().optional(),
  date_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  date_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_from: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  create_to: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),
  [names.staticDatumNameContains]: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
});

export type FilterSchema = z.infer<typeof filterSchema>;
export type PortfolioOption = {
  text: string;
  id: number;
  account_type?: string | null;
  currency?: string | null;
  reporting_currency?: string | null;
};

export type AssetOption = {
  text: string;
  id: number;
  currency?: string | null;
  asset_class_2?: string | null;
};


const optionalNumber = z.preprocess(
  (value) => (value === "" || value == null ? null : value),
  z.coerce.number().nullable().optional(),
);

const optionalAutocompleteField = z
  .preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      z.string().optional().nullable(),
      z.object({
        text: z.string(),
        id: z.number().optional(),
      }),
    ]),
  )
  .optional()
  .nullable();

export const FormSchema = z.object({
  date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
  [names.portfolioName]: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      z.string().optional().nullable(),
      z.object({
        text: z.string(),
        id: z.number(),
        account_type: z.string().nullable().optional(),
        currency: z.string().nullable().optional(),
        reporting_currency: z.string().nullable().optional(),
      }),
    ]),
  ),
  [names.staticDatumName]: z.preprocess(
    preprocessTextAsyncAutocompleteField,
    z.union([
      z.string().optional().nullable(),
      z.object({
        text: z.string(),
        id: z.number(),
        currency: z.string().nullable().optional(),
        asset_class_2: z.string().nullable().optional(),
      }),
    ]),
  ),
  [names.portfolioReportingCurrency]: z.string().nullable().optional(),
  [names.portfolioCurrency]: z.string().nullable().optional(),
  [names.staticDatumCurrency]: z.string().nullable().optional(),
  [names.portfolioAccountType]: z.string().nullable().optional(),
  [names.staticDatumAssetClass2]: z.string().nullable().optional(),
  client: optionalAutocompleteField,
  bank_name: optionalAutocompleteField,
  [names.portfolioId]: z.number(),
  [names.staticDatumId]: z.number(),
  market_value_local_ccy: optionalNumber,
  market_value_ptf_ccy: optionalNumber,
  fixing_local_ccy: optionalNumber,
  fixing_ptf_ccy: optionalNumber,
  quantity: optionalNumber,
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  time_serie_file_id: z.number().nullable().optional(),
});

type _BaseFormValues = z.infer<typeof FormSchema>;

export type TFormValues = Omit<
  _BaseFormValues,
  typeof names.portfolioName | typeof names.staticDatumName
> & {
  [K in typeof names.portfolioName]?: PortfolioOption | null;
} & {
  [K in typeof names.staticDatumName]?: AssetOption | null;
};
